import React, { useMemo } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import { observer } from 'mobx-react';
import { Route, Router, Switch, useParams } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { useStyles } from 'ui/base/theme';
import { BusyStore } from 'ui/base/button';
import { PasswordStore } from 'ui/base/input';
import { HistoryModalStore, Modal } from 'ui/base/modal';
import { StandardPage } from 'ui/base/page/standard_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';
import { Step, StepContent, StepLabel, Stepper } from 'ui/standard/stepper/stepper';
import { ActiveButton, DefaultButton } from 'ui/public/button';
import { LabeledInput, PasswordInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { ResultKind } from 'services/base/types';
import { Pangolin } from 'services/accounts/types';
import { IPangolinDomain, UpdateRelease } from 'services/pangolin/types';
import { UpdateUpgradeService } from 'services/pangolin/update_upgrade_service';

import { themedStyles } from '../manage_updates';
import { UpdateUpgradeStepper } from '../ui/update_upgrade_stepper';

import Messages from './update_upgrade_modal_messages';

export type UpdateUpgradeVersionModalProps = UpdateUpgradeModalProps;
export const UpdateUpgradeVersionModal = observer(({ modal, domain }: UpdateUpgradeVersionModalProps) => {
  const { id } = useParams<{ id: string }>();
  const styles = useStyles(themedStyles);

  const release = domain.updateReleases.get(id);

  return (
    <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.versionInfoTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldTextLeft} >
                {release?.tag}
                <div className={styles.fieldInfo}>
                  {Messages.versionInfoFieldVersion()}
                </div>
              </div>
              <img className={styles.fieldIcon}
                  src={styles.fieldIconImageVersion}
                  alt='Version Icon' />
            </div>
            {release?.notes && (
              <div className={styles.row}>
                <div className={classNames(styles.column, styles.corpus)}>
                  <div className={styles.label}>
                    {Messages.versionInfoFieldNotes()}
                  </div>
                  <div className={styles.value}>
                    <ReactMarkdown>{release.notes}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.section}>
            <DefaultButton type='button' onClick={modal.history.goBack}>
              {Messages.versionInfoButtonBack()}
            </DefaultButton>
          </div>
        </div>
      </StandardPage>
    </div>
  );
});

export type UpdateUpgradeConfirmModalProps = UpdateUpgradeModalProps;
export const UpdateUpgradeConfirmModal = observer(({ root, pangolin, domain, modal }: UpdateUpgradeConfirmModalProps) => {
  const { id: targetId } = useParams<{ id: string }>();

  const styles = useStyles(themedStyles);

  const sourceId = pangolin.updateActiveReleaseID;
  const sourceRelease = sourceId ? domain.updateReleases.get(sourceId) : undefined;
  const targetRelease = targetId ? domain.updateReleases.get(targetId) : undefined;

  const [busy] = React.useState(new BusyStore());
  const [passwordStore] = React.useState(new PasswordStore(''));
  const [passwordError, setPasswordError] = React.useState(' ');

  const disabled = passwordStore.value.length === 0;

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    setPasswordError(' ');

    if (!targetRelease) {
      setPasswordError(Messages.errorGeneric());
      return;
    }

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await UpdateUpgradeService.dispatch(
          domain, pangolin, targetRelease,
          passwordStore.value
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.kind === ResultKind.OK) {
      modal.dismissModal();
    } else if (result.status === 401) {
      // Display authorization error
      setPasswordError(Messages.errorUnauthorized());
    } else if (result.status === 402) {
      // Display plan required error
      setPasswordError(Messages.errorPlanRequired());
    } else {
      // Display generic error
      setPasswordError(Messages.errorGeneric());
    }
  }, [modal]);

  const onShowSourceModal = React.useCallback(() => {
    if (sourceRelease) modal.history.push(`${root}/info/releases/${sourceRelease.id}`);
  }, [modal, root, sourceRelease]);

  const onShowTargetModal = React.useCallback(() => {
    if (targetRelease) modal.history.push(`${root}/info/releases/${targetRelease.id}`);
  }, [modal, root, targetRelease]);

  return (
    <div className={classNames(styles.modalContainerLargest, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.confirmUpgradeTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <form className={styles.modalContentRow} onSubmit={onSubmit}>
          <div className={styles.section}>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft}>
                {pangolin.nickname}
                <div className={styles.fieldInfo}>
                  {Messages.confirmUpgradeFieldPangolin()}
                </div>
              </div>
              <ShieldIcon
                className={styles.fieldIcon}
                colorCode={pangolin.colorCode}
                size={ShieldSize.SMALL}
              />
              <div className={classNames(styles.open, styles.disabled)} />
            </div>
            {sourceRelease && (
              <div className={classNames(styles.field, styles.item, { [styles.clickable]: sourceRelease })} onClick={onShowSourceModal}>
                <div className={styles.fieldTextLeft}>
                  {sourceRelease.tag}
                  <div className={styles.fieldInfo}>
                    {Messages.confirmUpgradeFieldSource()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Source Icon'
                    src={styles.fieldIconImageSource} />
                <div className={classNames(styles.open, { [styles.disabled]: !sourceRelease })} />
              </div>
            )}
            <div className={classNames(styles.field, styles.item, { [styles.clickable]: targetRelease })} onClick={onShowTargetModal}>
              <div className={styles.fieldTextLeft}>
                {targetRelease?.tag}
                <div className={styles.fieldInfo}>
                  {Messages.confirmUpgradeFieldTarget()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Target Icon'
                   src={styles.fieldIconImageTarget} />
              <div className={classNames(styles.open, { [styles.disabled]: !targetRelease })} />
            </div>
          </div>
          <div className={styles.section}>
            <UpdateUpgradeStepper />
            <div className={styles.sectionTitle}>
              {Messages.confirmUpgradeFieldNotes()}
            </div>
            <ul className={styles.notes}>
              <li className={styles.item}>
                {Messages.confirmUpgradeNoteReboot()}
              </li>
            </ul>
            <LabeledInput label={Messages.confirmUpgradeInputPassword()}>
              <PasswordInput
                store={passwordStore}
                name='password'
                id='password'
              />
            </LabeledInput>
            <ErrorMessage message={passwordError}/>
            <ActiveButton busy={busy} disabled={disabled}>
              {Messages.confirmUpgradeButtonUpgrade()}
            </ActiveButton>
            <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
              {Messages.confirmUpgradeButtonBack()}
            </DefaultButton>
          </div>
        </form>
      </StandardPage>
    </div>
  );
});

export type UpdateUpgradeItemProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  release: UpdateRelease;
  modal: HistoryModalStore;
  root: string;
};
export const UpdateUpgradeItem = observer(({
  pangolin,
  domain,
  release,
  modal,
  root,
}: UpdateUpgradeItemProps) => {
  const styles = useStyles(themedStyles);

  const version = pangolin.updateVersion;

  const current = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const label = useMemo(() => {
    if (!release.notes) return '';
    const lfindex = release.notes.indexOf('\n');
    if (lfindex === undefined) return release.notes ?? '';
    if (lfindex < 0) return release.notes ?? '';
    return release.notes.substring(0, lfindex);
  }, [release.notes]);

  const position = useMemo(() => {
    const release_tag = release.tag;
    const current_tag = current?.tag ?? version;
    if (!current_tag) return 'future';
    const release_tag_int = parseInt(release_tag);
    const current_tag_int = parseInt(current_tag);
    if (release_tag_int > current_tag_int) return 'future';
    if (release_tag_int < current_tag_int) return 'previous';
    if (release_tag_int === current_tag_int) return 'current';
    return 'future';
  }, [current, version]);

  const stepStatus = (
    position === 'future' ? 'pending' :
    position === 'current' ? 'active' :
    'disabled'
  );

  const stepClassName = classNames({
    [styles.clickable]: position === 'future',
  });

  const openClassName = classNames(styles.open, {
    [styles.disabled]: position !== 'future',
  });

  const statusLabel = (
    position === 'future' ? Messages.firmwareListStatusAvailable() :
    position === 'current' ? Messages.firmwareListStatusCurrent() :
    Messages.firmwareListStatusPrevious()
  );

  const onClick = position === 'future' ? React.useCallback(() => {
    modal.history.push(`${root}/releases/${release.id}`);
  }, [modal, root, release]) : undefined;

  return (
    <Step status={stepStatus} className={stepClassName} onClick={onClick}>
      <StepLabel>
        <div className={styles.field}>
          <div className={styles.fieldTextRight}>
            {release.tag}
            <div className={styles.fieldInfo}>
              {statusLabel}
            </div>
          </div>
          <div className={openClassName}/>
        </div>
      </StepLabel>
      <StepContent>
        <div className={styles.field}>
          <div className={styles.fieldCorpusRight}>
            {label}
          </div>
          <div className={styles.pad}/>
        </div>
      </StepContent>
    </Step>
  );
});

export type UpdateUpgradeListModalProps = UpdateUpgradeModalProps;
export const UpdateUpgradeListModal = observer(({
  pangolin,
  domain,
  modal,
  root,
}: UpdateUpgradeListModalProps) => {
  const styles = useStyles(themedStyles);
  const releases = domain.updateReleases.values;
  return (
    <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.firmwareListTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <Stepper>
            {releases.map(release => (
              <UpdateUpgradeItem
                key={release.id}
                pangolin={pangolin}
                domain={domain}
                release={release}
                modal={modal}
                root={root}
              />
            ))}
          </Stepper>
        </div>
      </StandardPage>
    </div>
  );
});

export type UpdateUpgradeModalProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  version: string | null;
  modal: HistoryModalStore;
  root: string;
};
export const UpdateUpgradeModal = observer((props: UpdateUpgradeModalProps) => {
  const { modal, root } = props;
  return (
    <Modal store={modal} onBackdropClick={modal.history.goBack}>
      <Router history={modal.history}>
        <Switch>
          <Route exact path={`${root}/releases`}>
            <UpdateUpgradeListModal {...props} />
          </Route>
          <Route exact path={`${root}/releases/:id`}>
            <UpdateUpgradeConfirmModal {...props} />
          </Route>
          <Route exact path={`${root}/info/releases/:id`}>
            <UpdateUpgradeVersionModal {...props} />
          </Route>
        </Switch>
      </Router>
    </Modal>
  );
});
